/**
 * Layout primitives for the trainer dashboard — the replacement for
 * react-bootstrap's Container / Row / Col.
 *
 * The difference that matters: this is real CSS Grid, so the gutter is a
 * `gap` on the container rather than negative margins on the row and
 * padding on every cell. Cells therefore need no spacing classes of their
 * own, which is what let the old `mb-4 mb-lg-0` pairs go away.
 *
 * Spans are plain classes, one per breakpoint and width. Nothing here
 * declares a CSS variable.
 */

// Styles live in the single TrainerDashboard.css, imported by the page.

const cx = (...parts) => parts.filter(Boolean).join(' ');

/** Page shell — replaces <Container fluid>. */
export const Page = ({ className, children, ...rest }) => (
  <div className={cx('td-page', className)} {...rest}>{children}</div>
);

/**
 * Grid — replaces <Row>.
 * `gap`   : 'sm' | 'md' | 'lg'  (was g-3 / g-4)
 * `space` : 'sm' | 'md' | 'lg'  bottom margin (was mb-3 / mb-4 / mb-5)
 */
export const Grid = ({ gap = 'md', space, align, className, children, ...rest }) => (
  <div
    className={cx('td-grid', `td-grid--${gap}`, space && `td-space-${space}`,
                  align === 'center' && 'td-grid--center', className)}
    {...rest}
  >
    {children}
  </div>
);

/**
 * Cell — replaces <Col>. Spans are out of 12 and cascade upward: a cell
 * given only `lg` keeps that span at lg and above, and is full width below.
 */
export const Cell = ({ xs, sm, md, lg, xl, className, children, ...rest }) => (
  <div
    className={cx(
      'td-cell',
      xs && `td-cell-xs-${xs}`,
      sm && `td-cell-sm-${sm}`,
      md && `td-cell-md-${md}`,
      lg && `td-cell-lg-${lg}`,
      xl && `td-cell-xl-${xl}`,
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);
